import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Fix H: el login se reintenta solo si no hubo respuesta del servidor (red caida, timeout) o fue un 502/503/504.
// Una clave incorrecta (401), una cuenta bloqueada (429) o un 403 nunca se reintentan.
const LOGIN_RETRY_STATUS = [502, 503, 504];
const LOGIN_RETRY_DELAYS_MS = [800, 1600];
const LOGIN_TIMEOUT_MS = 15000;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function postLogin(email, password, onRetry) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const { data } = await api.post("/auth/login", { email, password }, { timeout: LOGIN_TIMEOUT_MS });
      return data;
    } catch (err) {
      const status = err.response?.status;
      const retryable = !status || LOGIN_RETRY_STATUS.includes(status);
      if (!retryable || attempt >= LOGIN_RETRY_DELAYS_MS.length) throw err;
      if (onRetry) onRetry(attempt + 1);
      await wait(LOGIN_RETRY_DELAYS_MS[attempt]);
    }
  }
}

export function AuthProvider({ children }) {
  // null = checking session, false = not authenticated, object = authenticated user
  const [user, setUser] = useState(null);
  // Fix H: cada ingreso o salida sube el contador; un /auth/me que estaba en vuelo antes no pisa al usuario nuevo.
  const epoch = useRef(0);

  const fetchMe = useCallback(async () => {
    const started = epoch.current;
    try {
      const { data } = await api.get("/auth/me");
      if (started === epoch.current) setUser(data);
    } catch (e) {
      if (started === epoch.current) setUser(false);
    }
  }, []);

  useEffect(() => {
    // /inscripcion/ es la pagina del alumno, sin sesion. Fix J (2026-10-27): "/" es la web publica, tampoco.
    if (window.location.pathname === "/" || window.location.pathname.startsWith("/inscripcion/")) {
      setUser(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password, onRetry) => {
    const data = await postLogin(email, password, onRetry);
    epoch.current += 1;
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore network errors on logout
    }
    epoch.current += 1;
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refreshUser: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
